import { Navigate, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';

import type { Scope } from '../api/types';
import { useAuth } from '../auth/useAuth';

interface RouteGuardProps {
  children: ReactNode;
  /** When provided, the user must have this scope or they are sent to /forbidden. */
  scope?: Scope;
  /** When true (default), unauthenticated users are redirected to /login. */
  requireAuth?: boolean;
}

/**
 * Guards a route by authentication state and/or required scope.
 *
 * - Not authenticated  → redirect to /login (preserving the requested URL).
 * - Missing scope      → redirect to /forbidden.
 * - All checks pass    → render children.
 */
export function RouteGuard({ children, scope, requireAuth = true }: RouteGuardProps): JSX.Element {
  const { isAuthenticated, hasScope } = useAuth();
  const location = useLocation();

  if (requireAuth && !isAuthenticated) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  if (scope !== undefined && !hasScope(scope)) {
    return <Navigate to="/forbidden" replace />;
  }

  return <>{children}</>;
}
